import React, { useState, useMemo, useEffect } from 'react';
import { Trophy, Search, Filter, ChevronDown, ChevronUp, Users, Award, Settings, X, Edit2, Save, XCircle } from 'lucide-react';
import { LineChart as RechartsLineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import type { MemberProfile } from '../../types/member';
import { syncMemberProfile, syncAllMembers, parseMembersData, getCachedMembers, updateMemberLocally, loadMembersFromDatabase } from '../../lib/members';
import { connectionManager } from '../../lib/supabase/connectionManager';
import { cacheManager } from '../../lib/cache';
import { CACHE_KEY } from '../../lib/members';

interface MembersProfileProps {
  members: MemberProfile[];
  isAdmin?: boolean;
  setMembers: (members: MemberProfile[]) => void;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
}

export function MembersProfile({ members, isAdmin, setMembers, searchTerm, setSearchTerm }: MembersProfileProps) {
  const [selectedFlight, setSelectedFlight] = useState<string>('all');
  const [sortField, setSortField] = useState<'name' | 'handicap' | 'flight'>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'all' | 'M' | 'NVM' | 'inactive' | 'null'>('all');
  const [seniorFilter, setSeniorFilter] = useState<'all' | 'senior' | 'regular'>('all');
  const [selectedMember, setSelectedMember] = useState<MemberProfile | null>(null);
  const [showScores, setShowScores] = useState(true);
  const [editingMember, setEditingMember] = useState<string | null>(null);
  const [chartData, setChartData] = useState<any[]>([]);
  const [editName, setEditName] = useState('');
  const [scoreData, setScoreData] = useState<any[]>([]);
  const [editStatus, setEditStatus] = useState<'M' | 'NVM' | 'inactive' | null>(null);
  const [editDivision, setEditDivision] = useState<'senior' | 'regular' | null>(null);
  const [subscriptionError, setSubscriptionError] = useState<string | null>(null);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [localMembers, setLocalMembers] = useState<MemberProfile[]>(members);
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRIES = 3;
  const RETRY_DELAY = 5000;

  // Update local state when props change
  useEffect(() => {
    if (searchTerm !== undefined) {
      setSearchTerm(searchTerm);
    }
  }, [searchTerm]);

  // Prepare chart data when member is selected
  useEffect(() => {
    if (selectedMember?.tournament_scores) {
      const data = selectedMember.tournament_scores
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
        .map((score, index, array) => {
          const scoreValue = typeof score.score === 'number' ? score.score : 
                           typeof score.strokes === 'number' ? score.strokes : 0;

          // Calculate linear regression points
          const n = array.length;
          const sumX = array.reduce((acc, _, i) => acc + i, 0);
          const sumY = array.reduce((acc, s) => acc + (s.score || s.strokes || 0), 0);
          const sumXY = array.reduce((acc, s, i) => acc + (i * (s.score || s.strokes || 0)), 0);
          const sumXX = array.reduce((acc, _, i) => acc + (i * i), 0);
          
          const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
          const intercept = (sumY - slope * sumX) / n;
          
          const trendValue = slope * index + intercept;

          return {
            date: score.date ? formatDate(score.date, {
              year: '2-digit',
              month: 'short'
            }) : '-',
            score: scoreValue,
            trend: trendValue,
            round: index + 1
          };
        });

      setChartData(data);
    }
  }, [selectedMember]);

  // Helper function to format dates consistently
  const formatDate = (dateStr: string, options?: Intl.DateTimeFormatOptions) => {
    const date = new Date(`${dateStr.split('T')[0]}T12:00:00Z`);
    return date.toLocaleDateString('en-US', options);
  };

  const handleStartEdit = (member: MemberProfile) => {
    setEditingMember(member.id);
    setEditName(member.name);
    setEditDivision(member.isSenior ? 'senior' : 'regular');
    // Ensure valid status value
    setEditStatus(member.currentStatus === 'M' || member.currentStatus === 'NVM' || member.currentStatus === 'inactive'
      ? member.currentStatus 
      : 'M');
    // Cache current state for rollback
    localStorage.setItem('edit_backup', JSON.stringify(member));
  };

  const handleSaveEdit = async (memberId: string) => {
    const trimmedName = editName.trim();
    
    // Validate name
    if (editName.trim() === '') {
      alert('Member name cannot be empty');
      return;
    }

    // Validate status
    if (!editStatus || !['M', 'NVM', 'inactive'].includes(editStatus)) {
      alert('Please select a valid member status (M, NVM, or Inactive)');
      return;
    }

    const member = members.find(m => m.id === memberId);
    if (!member) return;

    try {
      // Check for duplicate names
      const duplicateName = members.find(m => 
        m.id !== memberId && 
        m.name.toLowerCase() === trimmedName.toLowerCase()
      );
      
      if (duplicateName) {
        throw new Error(
          `Cannot update member: "${trimmedName}" already exists ` +
          `(current name: ${duplicateName.name})`
        );
      }

      console.log('Saving member edit:', {
        id: memberId,
        oldName: member.name,
        newName: trimmedName,
        oldStatus: member.currentStatus,
        newStatus: editStatus
      });

      const updatedMembers = members.map(m => {
        if (m.id === memberId) {
          return {
            ...m,
            name: trimmedName,
            currentStatus: editStatus,
            membershipStatus: editStatus === 'M' ? 'active' : editStatus === 'NVM' ? 'non-voting' : 'inactive',
            isSenior: editDivision === 'senior',
            tournament_scores: m.tournament_scores || [],
            total_rounds: m.total_rounds || 0
          };
        }
        return m;
      });

      setMembers(updatedMembers);
      
      // Find the updated member
      const updatedMember = updatedMembers.find(m => m.id === memberId);
      if (updatedMember) {
        // First try to sync to Supabase
        try {
          await syncMemberProfile(updatedMember);
          console.log('Successfully synced member to Supabase');
          
          // Only update local state after successful sync
          setMembers(updatedMembers);
          setLocalMembers(updatedMembers);
          updateMemberLocally(updatedMember);
          
          setEditingMember(null);
          setEditName('');
          setEditStatus(null);
        } catch (error) {
          console.error('Failed to sync member update:', error);
          
          // Restore previous state
          const backup = localStorage.getItem('edit_backup');
          if (backup) {
            const originalMember = JSON.parse(backup);
            const revertedMembers = members.map(m => 
              m.id === memberId ? originalMember : m
            );
            setMembers(revertedMembers);
            setLocalMembers(revertedMembers);
          }
          
          alert('Failed to update member. Please try again.');
          return;
        }
      }
    } catch (error) {
      console.error('Failed to update member:', error);
      alert(error instanceof Error ? error.message : 'Failed to update member. Please try again.');
    } finally {
      localStorage.removeItem('edit_backup');
    }
  };

  const handleCancelEdit = () => {
    setEditingMember(null);
    setEditName('');
    setEditDivision(null);
    setEditStatus(null);
  };

  // Initial data load and sync
  useEffect(() => {
    const loadMembers = async () => {
      // Only reset subscription state if we don't have data
      if (!members.length) {
        setIsSubscribed(false);
      }

      // Check cache first
      let cachedMembers = getCachedMembers();
      if (cachedMembers) {
        setMembers(cachedMembers);
        setLocalMembers(cachedMembers);
        setIsSubscribed(true); // Mark as subscribed since we have data
        return; // Exit early if we have cached data
      }

      try {
        // Only try database load if we have a connection
        if (navigator.onLine && connectionManager.isConnected()) {
          const dbMembers = await loadMembersFromDatabase();
          if (dbMembers) {
            setMembers(dbMembers);
            setLocalMembers(dbMembers);
            // Cache the results
            cacheManager.set(CACHE_KEY, dbMembers);
            setIsSubscribed(true); // Mark as subscribed after successful DB load
          }
        }
      } catch (error) {
        // Handle errors
        if (error instanceof Error) {
          // Use cached data as fallback
          if (cachedMembers) {
            setMembers(cachedMembers);
            setLocalMembers(cachedMembers);
            setIsSubscribed(true); // Mark as subscribed when using cache
          } else {
            // Try to parse from CSV as last resort
            try {
              const response = await fetch('/members.csv');
              const data = await response.text();
              const parsedMembers = parseMembersData(data);
              setMembers(parsedMembers);
              setLocalMembers(parsedMembers);
              cacheManager.set(CACHE_KEY, parsedMembers);
              setIsSubscribed(true);
            } catch (csvError) {
              console.error('Failed to load members from CSV:', csvError);
            }
          }
        }
      }
    };

    // Handle online/offline status
    const handleOnline = () => {
      setIsOffline(false);
      setSubscriptionError(null);
      // Reload data when coming back online
      console.log('Back online, reloading members data...');
      loadMembers();
    };

    const handleOffline = () => {
      setIsOffline(true);
      setIsSubscribed(false);
      console.log('Device is offline, using cached data');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Initial load
    console.log('Initial members data load...');
    loadMembers();

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const getSortIcon = (field: string) => {
    if (field !== sortField) return null;
    return sortDirection === 'asc' ? (
      <ChevronUp className="w-4 h-4 inline-block" />
    ) : (
      <ChevronDown className="w-4 h-4 inline-block" />
    );
  };

  const handleSort = (field: 'name' | 'handicap' | 'flight') => {
    if (field === sortField) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
      console.log('Changed sort direction:', sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
      console.log('Changed sort field to:', field);
    }
  };

  const filteredAndSortedMembers = useMemo(() => {
    return localMembers
      .filter(member => {
        const matchesSearch = member.name.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesFlight = selectedFlight === 'all' || member.flight === selectedFlight;
        const matchesStatus = statusFilter === 'all' || 
          (statusFilter === 'null' && !member.currentStatus) ||
          member.currentStatus === statusFilter;
        const matchesSenior = seniorFilter === 'all' || 
          (seniorFilter === 'senior' && member.isSenior) ||
          (seniorFilter === 'regular' && !member.isSenior);
        
        return matchesSearch && matchesFlight && matchesStatus && matchesSenior;
      })
      .sort((a, b) => {
        let comparison = 0;
        
        switch (sortField) {
          case 'name':
            comparison = a.name.localeCompare(b.name);
            break;
          case 'handicap':
            comparison = (a.handicap || 0) - (b.handicap || 0);
            break;
          case 'flight':
            comparison = (a.flight || 'Z').localeCompare(b.flight || 'Z');
            break;
        }
        
        return sortDirection === 'asc' ? comparison : -comparison;
      });
  }, [localMembers, searchTerm, selectedFlight, sortField, sortDirection, statusFilter, seniorFilter]);

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="space-y-4">
        {/* Search and Filters */}
        <div className="flex justify-end gap-4">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
          >
            <Filter className="w-5 h-5" />
            Filters
            {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Flight</label>
              <select
                value={selectedFlight}
                onChange={(e) => setSelectedFlight(e.target.value)}
                className="w-full rounded-md border-gray-300"
              >
                <option value="all">All Flights</option>
                <option value="Championship">Championship</option>
                <option value="A">A Flight</option>
                <option value="B">B Flight</option>
                <option value="C">C Flight</option>
                <option value="Sr">Senior</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as 'all' | 'M' | 'NVM' | 'inactive' | 'null')}
                className="w-full rounded-md border-gray-300"
              >
                <option value="all">All Status</option>
                <option value="null">No Status</option>
                <option value="M">Member</option>
                <option value="NVM">NVM</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Division</label>
              <select
                value={seniorFilter}
                onChange={(e) => setSeniorFilter(e.target.value as 'all' | 'senior' | 'regular')}
                className="w-full rounded-md border-gray-300"
              >
                <option value="all">All Divisions</option>
                <option value="regular">Regular</option>
                <option value="senior">Senior</option>
              </select>
            </div>
          </div>
        )}

        {filteredAndSortedMembers.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="text-center py-12">
              <Trophy className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <div className="text-center max-w-sm mx-auto">
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No Members Found
                </h3>
                <p className="text-sm text-gray-500">
                  Try adjusting your search or filters to find members.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Members Table */}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th 
                      className="px-0.5 py-1 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('name')}
                    >
                      Name {getSortIcon('name')}
                    </th>
                    <th 
                      className="px-0.5 py-1 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Status
                    </th>
                    <th 
                      className="px-0.5 py-1 text-right text-[10px] font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('handicap')}
                    >
                      HC {getSortIcon('handicap')}
                    </th>
                    <th 
                      className="px-0.5 py-1 text-center text-[10px] font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('flight')}
                    >
                      FL {getSortIcon('flight')}
                    </th>
                    {showScores && (
                      <th 
                        className="px-0.5 py-1 text-center text-[10px] font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Scores
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredAndSortedMembers.map((member) => (
                    <tr key={member.id} className="hover:bg-gray-50">
                      <td className="px-0.5 py-1">
                        <div className="flex items-center justify-between">
                          <div>
                            {editingMember === member.id ? (
                              <div className="flex items-center gap-2">
                                <input
                                  type="text"
                                  value={editName}
                                  onChange={(e) => setEditName(e.target.value)}
                                  className="text-xs border-gray-300 rounded-md w-24"
                                />
                                <div className="flex items-center gap-1">
                                  <button
                                    onClick={() => handleSaveEdit(member.id)}
                                    className="p-1 text-green-600 hover:text-green-700"
                                  >
                                    <Save className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={handleCancelEdit}
                                    className="p-1 text-red-600 hover:text-red-700"
                                  >
                                    <XCircle className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <div className="text-xs font-medium text-gray-900 leading-tight">
                                  {member.name.split(' ').map((part, i) => (
                                    <div key={i}>{part}</div>
                                  ))}
                                </div>
                                {isAdmin && (
                                  <button
                                    onClick={() => handleStartEdit(member)}
                                    className="p-1 text-gray-400 hover:text-gray-600"
                                  >
                                    <Edit2 className="w-3.5 h-3.5" />
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-0.5 py-1 whitespace-nowrap">
                        {editingMember === member.id ? (
                          <select
                            value={editStatus || ''}
                            onChange={(e) => setEditStatus(e.target.value as 'M' | 'NVM' | 'inactive' | null)}
                            className="text-[10px] border-gray-300 rounded-md"
                          >
                            <option value="">Select Status</option>
                            <option value="M">Member</option>
                            <option value="NVM">NVM</option>
                            <option value="inactive">Inactive</option>
                          </select>
                        ) : (
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
                            member.currentStatus === 'M'
                              ? 'bg-green-100 text-green-800'
                              : member.currentStatus === 'NVM'
                              ? 'bg-yellow-100 text-yellow-800'
                              : member.currentStatus === 'inactive'
                              ? 'bg-gray-100 text-gray-600'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {member.currentStatus === 'M' ? 'Member' : 
                             member.currentStatus === 'NVM' ? 'NVM' : 
                             member.currentStatus === 'inactive' ? 'Inactive' :
                             'Pending'}
                          </span>
                        )}
                        {editingMember === member.id && (
                          <select
                            value={editDivision || ''}
                            onChange={(e) => setEditDivision(e.target.value as 'senior' | 'regular')}
                            className="text-[10px] border-gray-300 rounded-md mt-1"
                          >
                            <option value="regular">Regular</option>
                            <option value="senior">Senior</option>
                          </select>
                        )}
                      </td>
                      <td className="px-0.5 py-1 whitespace-nowrap text-right">
                        <span className="text-xs text-gray-900">
                          {member.handicap || 'SCR'}
                        </span>
                      </td>
                      <td className="px-0.5 py-1 whitespace-nowrap text-center">
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
                          member.flight === 'Championship' ? 'bg-purple-100 text-purple-800' :
                          member.flight === 'A' ? 'bg-blue-100 text-blue-800' :
                          member.flight === 'B' ? 'bg-green-100 text-green-800' :
                          member.flight === 'C' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {member.flight || '-'} {member.isSenior && '(Sr)'}
                        </span>
                      </td>
                      {showScores && (
                        <td className="px-0.5 py-1 whitespace-nowrap text-center">
                          <button
                            disabled={!member.tournament_scores || member.tournament_scores.length === 0}
                            onClick={() => setSelectedMember(member)}
                            onMouseEnter={() => console.log('Member scores:', {
                              name: member.name,
                              scores: member.tournament_scores,
                              total: member.total_rounds
                            })}
                            className={`inline-flex items-center gap-0.5 px-1.5 py-1 text-[10px] rounded-md shadow-sm transition-colors ${
                              member.tournament_scores?.length
                                ? 'bg-primary-600 text-white hover:bg-primary-700'
                                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            }`}
                          >
                            <Trophy className={`w-3 h-3 ${member.tournament_scores?.length ? '' : 'opacity-50'}`} />
                            <span>{member.tournament_scores?.length || 0}</span>
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Results Count */}
            <div className="text-sm text-gray-500">
              Showing {filteredAndSortedMembers.length} of {members.length} members
            </div>
          </>
        )}
      </div>

      {/* Tournament Scores Modal */}
      {selectedMember && selectedMember.tournament_scores && selectedMember.tournament_scores.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full m-4 max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex justify-between items-center p-6 pb-0">
              <div>
                <div className="flex items-center gap-2">
                  <Trophy className="w-6 h-6 text-primary-600" />
                  <div>
                    <h3 className="text-lg font-semibold">{selectedMember.name}'s Tournament History</h3>
                    <div className="text-sm text-gray-600 flex items-center gap-1">
                      <span className="font-medium">{selectedMember.total_rounds || 0}</span> rounds played
                    </div>
                  </div>
                </div>
              </div>
              <button
                onClick={() => {
                  setSelectedMember(null);
                  setChartData([]);
                }}
                className="p-6 text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="overflow-y-auto flex-1 px-6 pb-6">
              {/* Score Graph */}
              <div className="mb-6 bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-sm font-medium text-gray-700">Score Progression</h4>
                </div>
                <div className="h-[300px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsLineChart
                      data={chartData}
                      margin={{ top: 10, right: 30, left: 10, bottom: 30 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis
                        dataKey="date"
                        angle={-45}
                        textAnchor="end"
                        height={70}
                        tick={{ fontSize: 12 }}
                      />
                      <YAxis domain={['dataMin - 2', 'dataMax + 2']} />
                      <Tooltip
                        contentStyle={{ 
                          background: 'white', 
                          border: '1px solid #e5e7eb', 
                          borderRadius: '0.375rem',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}
                        labelStyle={{ color: '#374151', fontWeight: 500 }}
                      />
                      <Line
                        type="monotone"
                        dataKey="score"
                        stroke="#0284c7"
                        strokeWidth={1.5}
                        dot={{ fill: '#0284c7', r: 3 }}
                        activeDot={{ r: 5 }}
                        name="Score"
                      />
                      <Line
                        type="monotone"
                        dataKey="trend"
                        stroke="#9ca3af"
                        strokeWidth={1}
                        strokeDasharray="5 5"
                        dot={false}
                        name="Trend"
                      />
                    </RechartsLineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tournament
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Score
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {selectedMember.tournament_scores
                    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                    .map((score, index) => (
                      <tr key={`${score.tournament_id}-${index}`} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {score.tournament_name || 'Unknown Tournament'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {score.date ? formatDate(score.date, {
                            year: 'numeric',
                            month: 'short'
                          }) : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-right text-primary-600">
                          {score.score || score.strokes || '-'}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}